import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { SITE, scrollToId } from "@/lib/site";

const line = {
    hidden: { y: "110%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
    }),
};

const scrollTo = scrollToId;

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
    const logoY = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

    const onMove = (e) => {
        const el = e.currentTarget;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = `rotateY(${px * 18}deg) rotateX(${-py * 18}deg)`;
    };
    const onLeave = (e) => {
        e.currentTarget.style.transform = "rotateY(0deg) rotateX(0deg)";
    };

    const headlines = ["EMPOWERING", "EVERY WORKER,", "EVERY FUTURE."];

    return (
        <section
            id="hero"
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden"
        >
            <div className="absolute inset-0 radial-glow" />
            <motion.div style={{ y }} className="absolute inset-0 pointer-events-none">
                <div className="absolute -right-32 top-1/4 h-[420px] w-[420px] rounded-full bg-[#1E5AA8]/20 blur-[120px]" />
                <div className="absolute left-1/4 bottom-0 h-[320px] w-[320px] rounded-full bg-[#F47B20]/15 blur-[120px]" />
            </motion.div>

            <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.3fr_1fr] gap-12 items-center w-full">
                <div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-7"
                    >
                        <span className="h-2 w-2 rounded-full bg-[#F47B20] animate-pulse" />
                        <span className="text-xs tracking-widest text-slate-300 uppercase">
                            Staffing & Placement • Est. {SITE.established}
                        </span>
                    </motion.div>

                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold text-white">
                        {headlines.map((h, i) => (
                            <span key={i} className="block overflow-hidden">
                                <motion.span
                                    custom={i}
                                    variants={line}
                                    initial="hidden"
                                    animate="show"
                                    className="block"
                                >
                                    {i === 2 ? (
                                        <span className="text-[#F47B20]">{h}</span>
                                    ) : (
                                        h
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.9, duration: 0.8 }}
                        className="mt-7 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
                    >
                        From ITI & Diploma holders to graduates, 10th/12th pass-outs and
                        first-time workers — we connect the right people with the right
                        companies across Pune & Maharashtra.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.05, duration: 0.6 }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={() => scrollTo("enquiry")}
                            data-testid="hero-job-seeker-cta"
                            className="group flex items-center gap-2 rounded-full bg-[#F47B20] px-7 py-3.5 font-semibold text-white orange-glow hover:bg-[#e06a10] hover:scale-105 transition-all"
                        >
                            I Need a Job
                            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            onClick={() => scrollTo("enquiry")}
                            data-testid="hero-employer-cta"
                            className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 font-semibold text-white hover:bg-white/10 hover:scale-105 transition-all"
                        >
                            I Need Employees
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2 }}
                        className="mt-8 flex items-center gap-2 text-sm text-slate-500"
                    >
                        <MapPin size={15} className="text-[#1E5AA8]" />
                        Pimpri Chinchwad, Pune, Maharashtra — {SITE.pin}
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: logoY, opacity }}
                    className="relative hidden lg:flex justify-center"
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, duration: 1 }}
                >
                    <div style={{ perspective: "1000px" }}>
                        <div
                            onMouseMove={onMove}
                            onMouseLeave={onLeave}
                            className="relative transition-transform duration-200 ease-out"
                            style={{ transformStyle: "preserve-3d" }}
                        >
                            <div className="absolute inset-0 rounded-full bg-[#1E5AA8]/30 blur-3xl scale-110" />
                            <img
                                src={SITE.logo}
                                alt="Bhoir Group emblem"
                                className="relative h-80 w-80 xl:h-96 xl:w-96 rounded-full object-cover ring-1 ring-white/10 shadow-2xl"
                            />
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
                                className="absolute -inset-6 rounded-full border border-dashed border-[#F47B20]/40"
                            />
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
